import { MapPin } from 'lucide-react'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useMyReferralShares } from 'src/features/business/referrals/api/referral-shares/use-my-referral-shares'
import { useReferralById } from 'src/features/business/referrals/api/referrals/use-referral-by-id'
import { ReferralListCard } from 'src/features/business/referrals/components/list-card'
import { ReferralShareActionModal } from 'src/features/business/referrals/components/lists/sent/referral-share-action-modal'
import { ReferralDetails } from 'src/features/business/referrals/components/referral-details'
import { DownArrow } from 'src/shared/components/icons/down-arrow'
import { Hourglass } from 'src/shared/components/icons/hourglass'
import { PointIcon } from 'src/shared/components/icons/point-icon'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'src/shared/components/ui/select'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import type { ReferralShare } from 'src/shared/schemas/referrals'
import { referralShareStatusMap } from 'src/shared/schemas/referrals'
import { formatTimeDelta, parseISODuration } from 'src/shared/utils/dates/format-time-delta'
import { formatPrintedReferralAddress } from 'src/shared/utils/format-field-value'

//TODO: refactor ReferralListCard to be different for user vs referral share vs referral share by profession
//TODO: clicking share needs action opens dialog to approve or deny
//TODO: Invite another button selects referral in store and opens /send-referral
//TODO: Archive Referral button
const SentReferralPage: React.FC = () => {
  const { referralId } = useParams<{ referralId: string }>()
  const { data: referral } = useReferralById(Number(referralId))
  const { data: interestedShares } = useMyReferralShares('sent', ['interested'], '', undefined, Number(referralId))
  const { data: approvedShares } = useMyReferralShares('sent', ['approved'], '', undefined, Number(referralId))
  const [selectedShare, setSelectedShare] = useState<ReferralShare | null>(null)
  const isMobile = useIsMobile()

  if (!referral) {
    throw new Response('Referral not found', { status: 404 })
  }

  return (
    <div className={`py-8 ${isMobile ? 'px-6' : 'px-12'} flex flex-col gap-8`}>
      <div className="flex flex-col items-center gap-4 text-center">
        <h1 className="text-4xl font-semibold">
          {referral.clientFirstName} {referral.clientLastName}
        </h1>
        <div className="flex items-center justify-center">
          {!isMobile && <MapPin className="mr-1 size-4 text-muted" />}
          <p className=" text-muted">{formatPrintedReferralAddress(referral, false)}</p>
        </div>
        {referral.referralTotalPoints != 0 && (
          <Badge
            variant="secondary"
            icon={PointIcon}
            text={`You've won ${referral.referralTotalPoints} points with this referral`}
            className="font-semibold"
          />
        )}
      </div>
      {interestedShares?.pages?.some((page) => (page ?? []).length > 0) && (
        <section className="mb-16">
          <h3 className="mb-4 inline-flex items-end text-xl font-semibold">
            Needs Action <DownArrow className="ml-2 size-4" />
          </h3>
          {interestedShares.pages
            .flatMap((page) => page ?? [])
            .map((share) => {
              const secondsLeft = typeof share.senderTimeLeft === 'string' ? parseISODuration(share.senderTimeLeft) : 0
              return (
                share && (
                  <>
                    <ReferralListCard
                      key={share.id}
                      title={`${share.receiverFirstName} ${share.receiverLastName}`}
                      subtitle={professionMap[share.receiverProfession].label}
                      avatarUrl={share.receiverProfileUrl ?? undefined}
                      showChat={true}
                      chatId={`referral-share-${share.id}`}
                      onClick={() => setSelectedShare(share)}
                      showActionSymbols={true}
                      tag={
                        share.senderTimeLeft !== null
                          ? formatTimeDelta(share.senderTimeLeft, { short: true, future: true })
                          : undefined
                      }
                      TagIcon={share.senderTimeLeft !== null ? Hourglass : undefined}
                      tagVariant={
                        share.senderTimeLeft !== null
                          ? share.senderTimeLeft < 0 ||
                            formatTimeDelta(share.senderTimeLeft, { short: true, future: true }).includes('overdue')
                            ? 'destructive'
                            : secondsLeft < 43200
                            ? 'destructive'
                            : secondsLeft < 172800
                            ? 'warning'
                            : 'outline'
                          : 'outline'
                      }
                    />
                    {selectedShare?.id === share.id && (
                      <ReferralShareActionModal
                        isOpen={true}
                        onClose={() => setSelectedShare(null)}
                        referralShare={share}
                      />
                    )}
                  </>
                )
              )
            })}
        </section>
      )}
      {approvedShares?.pages?.some((page) => (page ?? []).length > 0) && (
        <section className="mb-16">
          <h3 className="mb-4 inline-flex items-end text-xl font-semibold">
            Partners on this referral <DownArrow className="ml-2 size-4" />
          </h3>
          {approvedShares.pages
            .flatMap((page) => page ?? [])
            .map((share) => {
              return (
                share && (
                  <ReferralListCard
                    key={share.id}
                    title={`${share.receiverFirstName} ${share.receiverLastName}`}
                    subtitle={professionMap[share.receiverProfession].label}
                    avatarUrl={share.receiverProfileUrl ?? undefined}
                    showChat={true}
                    chatId={`referral-share-${share.id}`}
                    tag={typeof share.senderTotalPoints === 'number' ? share.senderTotalPoints.toString() : undefined}
                    TagIcon={typeof share.senderTotalPoints === 'number' ? PointIcon : undefined}
                    tagVariant="secondary"
                  />
                )
              )
            })}
        </section>
      )}

      <SharesByProfessionSection referralId={Number(referralId)} />

      <section className="mb-16 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="mb-4 inline-flex items-end text-xl font-semibold">
            Referral Info <DownArrow className="ml-2 size-4" />
          </h2>
        </div>
        <div className="space-y-4">
          <ReferralDetails referralId={Number(referralId)} status="approved" />
        </div>
      </section>
    </div>
  )
}

const SharesByProfessionSection: React.FC<{ referralId: number }> = ({ referralId }) => {
  const { data: referral } = useReferralById(Number(referralId))
  const isMobile = useIsMobile()

  // Get all professions that have non-approved shares
  const professionsWithPendingShares =
    referral?.referredProfessions?.filter((profession) => !referral.professionsApproved?.includes(profession)) ?? []

  // Initialize state with first pending profession or undefined if none
  const [selectedProfession, setSelectedProfession] = useState<Profession | undefined>(
    () => professionsWithPendingShares[0],
  )

  // Always call the hook with a valid profession or undefined
  const { data: sharesByProfession } = useMyReferralShares(
    'sent',
    ['pending', 'expired', 'sender_denied', 'receiver_declined'],
    undefined,
    undefined,
    referralId,
    selectedProfession,
  )

  // Early return if no data yet
  if (!referral) return null

  const hasSharesForSelectedProfession = sharesByProfession?.pages.some((page) => page && page.length > 0)

  // If no professions with pending shares or no shares for selected profession, don't show anything
  if (professionsWithPendingShares.length === 0 || !hasSharesForSelectedProfession) return null

  return (
    <section className="mb-16 flex flex-col gap-4">
      <div className="flex flex-row items-start gap-5">
        <h2 className="mb-4 text-xl font-semibold">View Shares To</h2>
        {selectedProfession && (
          <Select
            value={selectedProfession}
            defaultValue={selectedProfession}
            onValueChange={(value) => setSelectedProfession(value as Profession)}
          >
            <SelectTrigger className="h-10 w-48 rounded-full">
              <SelectValue defaultValue={selectedProfession} />
            </SelectTrigger>
            <SelectContent>
              {professionsWithPendingShares.map((profession) => (
                <SelectItem key={profession} value={profession}>
                  {professionMap[profession].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="space-y-4">
        {sharesByProfession?.pages
          .flatMap((page) => page ?? [])
          .filter((share) => share?.receiverProfession === selectedProfession)
          .map((share) => {
            return (
              // Refactor tag logic
              share && (
                <ReferralListCard
                  key={`${share.id}-${share.receiverProfession}`}
                  title={`${share.receiverFirstName} ${share.receiverLastName}`}
                  subtitle={professionMap[share.receiverProfession].label}
                  tag={
                    share.status === 'pending' && share.receiverTimeLeft !== null
                      ? formatTimeDelta(share.receiverTimeLeft, { short: isMobile, future: true })
                      : referralShareStatusMap[share.status].label
                  }
                  TagIcon={share.status === 'pending' && share.receiverTimeLeft !== null ? Hourglass : undefined}
                  tagVariant={
                    share.status === 'pending' && share.receiverTimeLeft !== null
                      ? share.receiverTimeLeft < 0 ||
                        formatTimeDelta(share.receiverTimeLeft, { short: isMobile, future: true }).includes('overdue')
                        ? 'destructive'
                        : typeof share.receiverTimeLeft === 'string' && parseISODuration(share.receiverTimeLeft) < 43200
                        ? 'destructive'
                        : typeof share.senderTimeLeft === 'string' && parseISODuration(share.senderTimeLeft) < 172800
                        ? 'warning'
                        : 'outline'
                      : 'default'
                  }
                />
              )
            )
          })}
      </div>
      {/* <Button
        onClick={() => {
          alert('TODO: Invite more logic')
        }}
      >
        Invite another {selectedProfession.replace(/_/g, ' ')}
      </Button> */}
    </section>
  )
}

// const PageMenu: React.FC = () => {
//   return (
//     <DropdownMenu>
//       <DropdownMenuTrigger asChild>
//         <Button>...</Button>
//       </DropdownMenuTrigger>
//       <DropdownMenuContent>
//         <DropdownMenuItem
//           onClick={() => {
//             alert('TODO: Close referral logic')
//           }}
//         >
//           Close Referral
//         </DropdownMenuItem>
//         <DropdownMenuItem
//           onClick={() => {
//             alert('TODO: Invite more logic')
//           }}
//         >
//           Invite More
//         </DropdownMenuItem>
//       </DropdownMenuContent>
//     </DropdownMenu>
//   )
// }

export default SentReferralPage
